import { useEffect, useId, useRef, useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { businessApi } from '@/lib/api-paths';
import { LogOut } from 'lucide-react';
import { PanelShell } from '@/components/panel-shell';
import { PageContainer } from '@/components/page-container';
import { PageError } from '@/components/page-error';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/inspector/toggle';
import { api } from '@/lib/api';
import { invalidateMe, useMe } from '@/hooks/use-me';

// =====================================================================
// Settings — "/app/settings" (WP1 Task 10). Account-level info that today
// lives ONLY in the sidebar footer (panel-shell.tsx) — name, email, avatar,
// org name, sign-out — now also gets its own page, reached from the panel's
// footer nav (businessRoutes.settings(businessId)). The sidebar footer itself is not touched
// by this task; this is an ADDITIONAL surface, not a replacement.
//
// Org-level route (like billing.tsx and locations.tsx) — account settings
// aren't scoped to one business/site. WP1 Task 15 fix round 1: swapped the
// old top-nav DashboardShell for PanelShell, which resolves the sidebar's
// Business from the canonical route; there is no stored-business fallback
// param (same pattern the survey screens use — see panel-shell.tsx).
//
// NOTIFICATIONS (2026-08-24, adversarial review ruling R6). Every email we
// send says "turn it back on any time in settings → notifications", and the
// unsubscribe page has said so since before the milestone stream existed —
// but this page had no such section, so the sentence was false for all three
// scopes. The toggles are the SIGNED-IN MEMBER'S OWN (T1.8: one row per
// user per business, businessApi(businessId, 'member-prefs')) — turning the
// digest off here silences it for you, not for a colleague. Same endpoint the
// wizard's reputation/pulse steps write, and the markup mirrors those steps so
// the two surfaces cannot drift apart visually.
//
// SAVES ON CHANGE, not on a button: there is no Continue here, and a
// preference the owner flipped and then navigated away from must not be lost.
// Each PATCH carries ONLY the field that changed (the endpoint is a genuine
// partial patch), so a save can never rewrite a sibling toggle.
//
// T2.7 (Phase 2) — the state is TRUTHFUL and PER BUSINESS:
//   * every member (owner, manager, editor — Location-scoped or not) sees and
//     edits their OWN toggles here; nothing on this card depends on a business
//     permission and there is no member selector;
//   * a switch shows only what the server confirmed: while a save is in flight
//     it stays at the persisted value, every switch ignores input (aria-disabled,
//     never native `disabled`, so keyboard focus is kept) and "Saving…" is
//     announced; the new value appears from the PATCH response ("Saved"), and
//     a failed save leaves the persisted value in place and says so;
//   * saves are one at a time, so two quick flips can never race — and a
//     module-level registry of in-flight saves per Business makes a re-read
//     (a remount on navigation, A→B→A, a retry) wait for that Business's
//     pending save, so the card can never show a value the save then replaces;
//   * a failed LOAD is an error with a retry — never the defaults dressed up
//     as the member's saved choice;
//   * the load and every save are tagged with a GENERATION that changes with
//     the Business in the URL: a late answer for Business A can never paint,
//     revert or unlock Business B's card, and until B answers the card shows
//     its loading state rather than A's values.
// =====================================================================

function userInitials(name: string | null, email: string): string {
  const source = name?.trim() || email;
  return source
    .split(/\s+|@/)
    .filter(Boolean)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');
}

interface EmailPrefs {
  weeklyDigest: boolean;
  reviewNotifications: boolean;
  milestones: boolean;
}

type PrefKey = keyof EmailPrefs;

const PREF_ROWS: ReadonlyArray<{ key: PrefKey; label: string; help: string }> = [
  {
    key: 'reviewNotifications',
    label: 'Review notifications',
    help: 'Email me when there are new reviews or review activity.',
  },
  {
    key: 'weeklyDigest',
    label: 'Weekly digest',
    help: 'Send me a weekly summary for this business.',
  },
  {
    // Kept from T1.8 (not one of T2.7's two locked controls): the unsubscribe
    // page promises "settings → notifications" for this stream too.
    key: 'milestones',
    label: 'Email me when something real happens on your profile or map',
    help: "At most one a week, after your first 48 hours: your first call, direction or website tap from Google, first appearances in the map's top three, review-count steps, and the first time an AI engine cites you.",
  },
];

/** Accept ONLY a well-formed answer: three real booleans. Anything else is a failed read, never a guess. */
function parsePrefs(raw: unknown): EmailPrefs | null {
  const p = raw && typeof raw === 'object' ? (raw as { prefs?: unknown }).prefs : null;
  if (!p || typeof p !== 'object') return null;
  const o = p as Record<string, unknown>;
  if (typeof o['weeklyDigest'] !== 'boolean' || typeof o['reviewNotifications'] !== 'boolean' || typeof o['milestones'] !== 'boolean') return null;
  return { weeklyDigest: o['weeklyDigest'], reviewNotifications: o['reviewNotifications'], milestones: o['milestones'] };
}

export type Load = { generation: number; status: 'loading' } | { generation: number; status: 'error' } | { generation: number; status: 'ready'; prefs: EmailPrefs };

/** What the card may render: a state from an earlier generation, or one fetched for
 *  another Business, is never shown — until THIS Business answers, it is loading.
 *  (Pure, so the guard is tested directly: the render between a Business change and
 *  its effect is not observable through the DOM.) */
export function visibleLoad(load: Load, generation: number, businessOfGeneration: string | null, businessId: string): Load {
  return load.generation === generation && businessOfGeneration === businessId ? load : { generation, status: 'loading' };
}

/** In-flight saves per Business, at MODULE scope: the page remounts on every
 *  navigation (PanelShell keys its boundary on the path), so a component-local
 *  ref would forget a save still on the wire. A read for a Business waits for
 *  that Business's pending save; a new save queues behind it. */
const inflightSaves = new Map<string, Promise<void>>();
/** test-only: forget every pending save between test cases */
export function resetInflightSaves(): void {
  inflightSaves.clear();
}

/** Shown to a member whose role in THIS business is confirmed manager/editor:
 *  review, digest and milestone emails are delivered to owners only (Phase 2
 *  decision), so the switches save a real preference that does not send yet. */
export const OWNER_ONLY_DELIVERY_NOTE = 'These emails are sent to business owners only. Your choices are saved for this business.';

/** Exported for its own test: the surrounding page needs `useMe` + PanelShell,
 *  and the behaviour worth pinning is this block's load / toggle / PATCH.
 *  `ownerOnlyDeliveryNote` is decided by the page from the member's confirmed
 *  role; it changes copy only — never the controls or what is saved. */
export function NotificationSettings({ ownerOnlyDeliveryNote = false }: { ownerOnlyDeliveryNote?: boolean } = {}) {
  // The Business API is addressed by the tenant the canonical route proved.
  // It is never derived from a Location id or a stored preference.
  const businessId = useBusinessId() ?? '';
  // One generation per (Business, load attempt). Every async answer checks it.
  const generation = useRef(0);
  const [attempt, setAttempt] = useState(0);
  const [load, setLoad] = useState<Load>({ generation: -1, status: 'loading' });
  const [saving, setSaving] = useState<PrefKey | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const [saved, setSaved] = useState(false);
  const businessOfGeneration = useRef<string | null>(null);
  const helpId = useId();

  useEffect(() => {
    const gen = ++generation.current;
    businessOfGeneration.current = businessId;
    setLoad({ generation: gen, status: 'loading' });
    setSaving(null);
    setSaveFailed(false);
    setSaved(false);
    // read only AFTER any save still on the wire for this Business has settled
    (inflightSaves.get(businessId) ?? Promise.resolve())
      .then(() => {
        if (generation.current !== gen) return undefined;
        return api.get<unknown>(businessApi(businessId, 'member-prefs')).then((res) => {
          if (generation.current !== gen) return;
          const prefs = parsePrefs(res);
          setLoad(prefs ? { generation: gen, status: 'ready', prefs } : { generation: gen, status: 'error' });
        });
      })
      .catch(() => {
        if (generation.current === gen) setLoad({ generation: gen, status: 'error' });
      });
  }, [businessId, attempt]);

  // A state from an earlier generation (another Business, or a superseded load)
  // is never rendered: until this Business answers, the card is loading.
  const current = visibleLoad(load, generation.current, businessOfGeneration.current, businessId);

  function toggle(key: PrefKey, next: boolean) {
    if (current.status !== 'ready' || saving !== null) return;
    const gen = generation.current;
    const target = businessId;
    setSaving(key);
    setSaveFailed(false);
    setSaved(false);
    // ONLY the changed field: the endpoint patches what it is given. Queued behind any
    // save still on the wire for this Business (e.g. from before a remount).
    const prior = inflightSaves.get(target);
    const send = () => api.patch<unknown>(businessApi(target, 'member-prefs'), { [key]: next });
    const run = prior ? prior.then(send) : send(); // nothing queued → sent now, not a tick later
    const settled = run.then(
      () => undefined,
      () => undefined,
    );
    inflightSaves.set(target, settled);
    void settled.then(() => {
      if (inflightSaves.get(target) === settled) inflightSaves.delete(target);
    });
    run
      .then((res) => {
        if (generation.current !== gen) return;
        const prefs = parsePrefs(res);
        if (prefs) {
          setLoad({ generation: gen, status: 'ready', prefs });
          setSaved(true);
        } else setSaveFailed(true);
      })
      .catch(() => {
        if (generation.current === gen) setSaveFailed(true);
      })
      .finally(() => {
        if (generation.current === gen) setSaving(null);
      });
  }

  return (
    <section className="rounded-xl border border-border-subtle bg-card p-6" aria-labelledby="email-notifications-heading">
      <h2 id="email-notifications-heading" className="text-sm font-semibold text-foreground">
        Email notifications
      </h2>
      {ownerOnlyDeliveryNote && <p className="mt-1 text-sm text-text-secondary">{OWNER_ONLY_DELIVERY_NOTE}</p>}
      {current.status === 'loading' && (
        <p role="status" aria-live="polite" className="mt-4 text-sm text-text-secondary">
          Loading your preferences…
        </p>
      )}
      {current.status === 'error' && (
        <div className="mt-4">
          <p role="alert" className="text-sm text-danger-fg">
            Couldn&rsquo;t load your notification preferences.
          </p>
          <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => setAttempt((a) => a + 1)}>
            Try again
          </Button>
        </div>
      )}
      {current.status === 'ready' && (
        <>
          {saveFailed && (
            <p role="alert" className="mt-2 text-sm text-danger-fg">
              Couldn&rsquo;t save that — please try again
            </p>
          )}
          <p role="status" aria-live="polite" className="mt-2 min-h-5 text-sm text-text-secondary">
            {saving !== null ? 'Saving…' : saved ? 'Saved' : ''}
          </p>
          <div className="mt-2 space-y-5">
            {PREF_ROWS.map((row) => (
              <div key={row.key}>
                {/* T12 controller ruling R2: a notification PREFERENCE takes
                    effect immediately — a switch, not a checkbox. */}
                <Toggle
                  label={row.label}
                  checked={current.prefs[row.key]}
                  pending={saving !== null}
                  describedBy={`${helpId}-${row.key}`}
                  onChange={(next) => toggle(row.key, next)}
                />
                <p id={`${helpId}-${row.key}`} className="mt-2 text-sm text-text-secondary">
                  {row.help}
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export function Settings() {
  const meState = useMe();
  const businessId = useBusinessId();

  if (meState.kind === 'error') {
    // Keeps the page's own identity — same eyebrow and <h1> as the ready
    // state below — when /api/me fails. See components/page-error.tsx.
    return (
      <div className="px-6 py-8">
        {/* NOT wrapped in <PanelShell>: the shell reads the sidebar out of the
            very /api/me payload that just failed, and its own error branch
            returns BEFORE its children — mounting it here would replace this
            header with the shell's bare paragraph and lose exactly the page
            identity this exists to keep. The wrapper restores the gutter
            PanelShell's <main> would have supplied (PageContainer sets none). */}
        <PageError
          eyebrow="Account"
          title="Settings"
          message={meState.message}
          retry={invalidateMe}
        />
      </div>
    );
  }
  if (meState.kind !== 'ready') return null;

  const { user, org } = meState.me;
  // The member's role in THIS business, as the server derived it from memberships.
  // Only a CONFIRMED manager/editor sees the owner-only delivery note; an unknown
  // role shows nothing rather than a claim we cannot back.
  const role = meState.me.businesses.find((b) => b.id === businessId)?.role;
  const ownerOnlyDeliveryNote = role === 'manager' || role === 'editor';
  const displayName = user.name?.trim() || user.email;
  const initials = userInitials(user.name, user.email);

  return (
    <PanelShell>
      {/* The bespoke `max-w-2xl … px-6 py-8` wrapper is gone: PanelShell's
          <main> already supplies the gutter, and doubling it is where the
          audit's "Settings column offset 72 px" came from. */}
      <PageContainer>
        <PageHeader eyebrow="Account" title="Settings" />

        <section className="flex items-center gap-4 rounded-xl border border-border-subtle bg-card p-6">
          {user.imageUrl ? (
            <img
              src={user.imageUrl}
              alt=""
              className="h-14 w-14 rounded-full border"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent text-lg font-semibold text-accent-foreground">
              {initials}
            </div>
          )}
          <div>
            <p className="font-medium text-foreground">{displayName}</p>
            <p className="text-sm text-text-secondary">{user.email}</p>
          </div>
        </section>

        <section className="rounded-xl border border-border-subtle bg-card p-6">
          <h2 className="text-sm font-semibold text-foreground">Organization</h2>
          <p className="mt-1 text-sm text-text-secondary">{org.name}</p>
        </section>

        <NotificationSettings ownerOnlyDeliveryNote={ownerOnlyDeliveryNote} />

        <section className="rounded-xl border border-border-subtle bg-card p-6">
          <h2 className="text-sm font-semibold text-foreground">Sign out</h2>
          <p className="mt-1 text-sm text-text-secondary">
            You&rsquo;ll need to sign back in with Google or Apple to get back in.
          </p>
          <form method="post" action="/auth/sign-out" className="mt-3">
            <Button type="submit" variant="outline" size="sm">
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </form>
        </section>
      </PageContainer>
    </PanelShell>
  );
}

export default Settings;
